/**
 * Billing Modal — Plan upgrade/switch and credit pack selection.
 *
 * Displays plan options or credit pack options in a modal overlay.
 * When the user picks an option, dispatches a `billing-checkout` event.
 * The parent (consumer-app) creates the Stripe Checkout session and redirects,
 * or calls updateSubscription for plan switches.
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { CREDIT_PACKS, PLAN_LABELS, type CreditPackId, type SubscriptionPlan } from "../services/index.ts";

export type BillingModalMode = "upgrade" | "buy-credits";

const PLAN_RANK: Record<string, number> = { free: 0, byok: 0.5, pro: 1, pro_plus: 2 };

@customElement("katanoid-billing-modal")
export class KatanoidBillingModal extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  @property({ type: String }) mode: BillingModalMode = "upgrade";
  @property({ type: String }) currentPlan: SubscriptionPlan = "free";
  @property({ attribute: false }) pendingPlan: SubscriptionPlan | null = null;
  @property({ type: String }) currentPeriodEnd: string | null = null;
  @state() private loading = false;

  protected override render(): TemplateResult {
    return html`
      <div class="billing-backdrop" @click=${this.handleClose}></div>
      <div class="billing-modal">
        <div class="billing-modal-header">
          <h2 class="billing-modal-title">
            ${this.mode === "upgrade" ? "Choose a Plan" : "Buy Credits"}
          </h2>
          <button class="btn btn--ghost btn--sm" @click=${this.handleClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </button>
        </div>

        <div class="billing-modal-body">
          ${this.loading
            ? html`<div style="display: flex; justify-content: center; padding: var(--space-8);"><div class="spinner"></div></div>`
            : this.mode === "upgrade"
              ? this.renderPlanOptions()
              : this.renderCreditPacks()
          }
        </div>
      </div>
    `;
  }

  /** Get the button label for switching to a plan */
  private getPlanButtonLabel(targetPlan: "pro" | "pro_plus" | "byok"): string {
    const currentRank = PLAN_RANK[this.currentPlan] ?? 0;
    const targetRank = PLAN_RANK[targetPlan] ?? 0;
    const label = PLAN_LABELS[targetPlan];

    if (this.currentPlan === "free") {
      return targetPlan === "byok" ? `Get ${label}` : `Upgrade to ${label}`;
    }
    return targetRank > currentRank ? `Upgrade to ${label}` : `Downgrade to ${label}`;
  }

  /** Render a single plan card */
  private renderPlanCard(
    plan: "byok" | "pro" | "pro_plus",
    price: string,
    features: string[],
    featured = false
  ): TemplateResult {
    const isCurrent = this.currentPlan === plan;
    const isPending = this.pendingPlan === plan;

    // Determine badge
    let badge = nothing as typeof nothing | TemplateResult;
    if (isCurrent) {
      badge = html`<div class="billing-plan-badge billing-plan-badge--current">Current Plan</div>`;
    } else if (isPending) {
      badge = html`<div class="billing-plan-badge billing-plan-badge--current">Scheduled</div>`;
    } else if (featured) {
      badge = html`<div class="billing-plan-badge">Best Value</div>`;
    }

    // Card class
    const cardClass = isCurrent || isPending
      ? "billing-plan-card billing-plan-card--current"
      : featured
        ? "billing-plan-card billing-plan-card--featured"
        : "billing-plan-card";

    return html`
      <div class="${cardClass}">
        ${badge}
        <div class="billing-plan-name">${PLAN_LABELS[plan]}</div>
        <div class="billing-plan-price">${price}<span class="billing-plan-period">/month</span></div>
        <ul class="billing-plan-features">
          ${features.map((f) => html`<li>${f}</li>`)}
        </ul>
        ${isCurrent
          ? this.pendingPlan
            ? html`
                <button
                  class="btn btn--primary btn--full"
                  @click=${this.handleCancelDowngrade}
                >
                  Keep ${PLAN_LABELS[plan]}
                </button>
              `
            : nothing
          : isPending
            ? html`
                <button
                  class="btn btn--ghost btn--full"
                  @click=${this.handleCancelDowngrade}
                >
                  Cancel Downgrade
                </button>
              `
            : html`
                <button
                  class="btn btn--primary btn--full"
                  @click=${() => this.selectPlan(plan)}
                >
                  ${this.getPlanButtonLabel(plan)}
                </button>
              `
        }
      </div>
    `;
  }

  private renderPlanOptions(): TemplateResult {
    const hasDowngrade = !!this.pendingPlan;

    return html`
      <div class="billing-plans">
        ${this.renderPlanCard("byok", "$5", [
          "Bring your own API key",
          "0 credits included",
          "400+ models via OpenRouter",
          "Unlimited channels",
        ])}
        ${this.renderPlanCard("pro", "$12", [
          "1,000 credits/month",
          "Unlimited channels",
          "Web browsing & voice",
          "Full memory",
        ])}
        ${this.renderPlanCard("pro_plus", "$24", [
          "1,500 credits/month",
          "Everything in Pro",
          "Priority responses",
          "Multi-assistant",
        ], true)}
      </div>
      ${hasDowngrade && this.currentPeriodEnd
        ? html`<p class="billing-pack-note">Downgrades take effect on ${new Date(this.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}.</p>`
        : nothing}
    `;
  }

  private renderCreditPacks(): TemplateResult {
    return html`
      <div class="billing-packs">
        ${CREDIT_PACKS.map(
          (pack) => html`
            <div class="billing-pack-card">
              <div class="billing-pack-credits">${pack.credits.toLocaleString()} credits</div>
              <div class="billing-pack-price">${pack.label}</div>
              <button
                class="btn btn--primary btn--full btn--sm"
                @click=${() => this.selectPack(pack.id)}
              >
                Buy
              </button>
            </div>
          `
        )}
      </div>
      <p class="billing-pack-note">
        Credit packs are added to your bonus balance and don't expire monthly.
      </p>
    `;
  }

  private handleCancelDowngrade(): void {
    this.dispatchEvent(
      new CustomEvent("billing-cancel-downgrade", {
        bubbles: true,
        composed: true,
      })
    );
  }

  private selectPlan(plan: "pro" | "pro_plus" | "byok"): void {
    this.loading = true;
    this.dispatchEvent(
      new CustomEvent("billing-checkout", {
        detail: { type: "subscription", plan },
        bubbles: true,
        composed: true,
      })
    );
  }

  private selectPack(packSize: CreditPackId): void {
    this.loading = true;
    this.dispatchEvent(
      new CustomEvent("billing-checkout", {
        detail: { type: "credit-pack", packSize },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleClose(): void {
    this.dispatchEvent(
      new CustomEvent("billing-close", { bubbles: true, composed: true })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-billing-modal": KatanoidBillingModal;
  }
}
